import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { graphBuilderApi } from '../../../services/api';

function formatCentral(isoStr) {
  if (!isoStr) return '';
  return new Date(isoStr).toLocaleString('en-US', {
    timeZone: 'America/Chicago',
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
    timeZoneName: 'short',
  });
}

const RAW_RESULTS_PREVIEW_LINES = 200;

export default function ABResultsPanel({ executionState, nodeResults, reportStatus, executionId }) {
  const [expanded, setExpanded] = useState(true);
  const [localReport, setLocalReport] = useState(null);
  const [checking, setChecking] = useState(false);
  const [showRawResults, setShowRawResults] = useState(false);
  const [showAllRawResults, setShowAllRawResults] = useState(false);

  const effectiveReport = (localReport?.available ? localReport : null) || reportStatus || localReport;

  const checkReport = useCallback(async () => {
    if (!executionId) return;
    setChecking(true);
    try {
      const rs = await graphBuilderApi.getReportStatus(executionId);
      setLocalReport(rs);
    } catch (_) { /* ignore */ }
    setChecking(false);
  }, [executionId]);

  useEffect(() => {
    if (executionId && !reportStatus) {
      checkReport();
    }
  }, [executionId, reportStatus, checkReport]);

  const rawResultsStr = useMemo(() => {
    if (!showRawResults || !nodeResults) return null;
    return JSON.stringify(nodeResults, null, 2);
  }, [showRawResults, nodeResults]);

  const rawResultsPreview = useMemo(() => {
    if (!rawResultsStr || showAllRawResults) return rawResultsStr;
    const lines = rawResultsStr.split('\n');
    if (lines.length <= RAW_RESULTS_PREVIEW_LINES) return rawResultsStr;
    return lines.slice(0, RAW_RESULTS_PREVIEW_LINES).join('\n') + '\n...';
  }, [rawResultsStr, showAllRawResults]);

  if (!executionState) return null;

  const status = executionState.status;
  const isTerminal = ['completed', 'failed', 'cancelled'].includes(status);
  if (!isTerminal) return null;

  const completedCount = (executionState.completed_nodes || []).length;
  const failedCount = (executionState.failed_nodes || []).length;
  const skippedCount = (executionState.skipped_nodes || []).length;
  const totalDuration = executionState.started_at && executionState.completed_at
    ? ((new Date(executionState.completed_at) - new Date(executionState.started_at)) / 1000).toFixed(1)
    : null;

  const finalOutputs = executionState.final_outputs || {};

  const handleDownload = () => {
    const url = graphBuilderApi.getReportDownloadUrl(executionId);
    window.open(url, '_blank');
  };

  return (
    <div className="ab-results-panel">
      <div className="results-header" onClick={() => setExpanded(!expanded)}>
        <h3>
          {expanded ? '▾' : '▸'} Results
          <span className={`status-pill ${status}`}>{status}</span>
        </h3>
        <div className="stats-row">
          <span className="stat"><strong>{completedCount}</strong> completed</span>
          {failedCount > 0 && <span className="stat fail"><strong>{failedCount}</strong> failed</span>}
          {skippedCount > 0 && <span className="stat skip"><strong>{skippedCount}</strong> skipped</span>}
          {totalDuration && <span className="stat"><strong>{totalDuration}s</strong> total</span>}
          {executionState.started_at && (
            <span className="stat">{formatCentral(executionState.started_at)}</span>
          )}
        </div>
      </div>

      {expanded && (
        <div className="results-body">
          {/* PDF Download */}
          <div className="results-actions">
            {effectiveReport?.available ? (
              <button className="download-btn" onClick={handleDownload}>
                Download PDF Report
              </button>
            ) : effectiveReport?.generating || checking ? (
              <button className="download-btn generating" disabled>
                <span className="spinner-small" /> {checking ? 'Checking...' : 'Generating Report...'}
              </button>
            ) : (
              <button className="download-btn" onClick={checkReport}>
                Check Report
              </button>
            )}
          </div>

          {/* Final adjudication / outputs */}
          {Object.keys(finalOutputs).length > 0 && (
            <div className="raw-output-section">
              <h4>Final Outputs</h4>
              <pre className="code-block">
                {JSON.stringify(finalOutputs, null, 2)}
              </pre>
            </div>
          )}

          {/* Raw node results (lazy -- only stringify on demand) */}
          {nodeResults && (
            <div className="raw-output-section">
              <h4
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => { setShowRawResults(v => !v); setShowAllRawResults(false); }}
              >
                {showRawResults ? '▾' : '▸'} Raw Node Results ({Object.keys(nodeResults).length} nodes)
              </h4>
              {showRawResults && rawResultsPreview && (
                <>
                  <pre className="code-block">{rawResultsPreview}</pre>
                  {rawResultsStr && rawResultsStr !== rawResultsPreview && !showAllRawResults && (
                    <button
                      className="download-btn"
                      style={{ marginTop: 4, fontSize: 12 }}
                      onClick={() => setShowAllRawResults(true)}
                    >
                      Show All ({Math.round(rawResultsStr.length / 1024)} KB)
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
